import { IconButton, RotateCcwIcon, Scrollbar, Spinner } from "@carbon6/c6-frontend-design";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import {
  inlineLoadingLineClassName,
  inlineSpinnerClassName,
} from "@/shared/ui/loadingState/public";
import { AppliedFilterChips } from "./AppliedFilterChips";
import { isPresetActive, isPresetAvailable } from "./datasetFilters";
import { resolveFilterRail, type DatasetFilterSurface } from "./datasetFilterBookmarks";
import { applyPreset, FacetControl } from "./FacetControl";
import type { DatasetFilterController } from "./DatasetFilterController";
import { countFilterPredicates } from "./labelFilterQuery";
import { MatchModeChip } from "./MatchModeChip";
import styles from "./DatasetFilter.module.css";

export type DatasetFilterProps = {
  controller: DatasetFilterController;
  className?: string;
  // 북마크 저장 스코프다. 화면마다 따로 저장한다. Library/Archived 는 같은 화면이라 datasetLibrary 를 함께 쓴다.
  bookmarkSurface?: DatasetFilterSurface;
  // uid 스코프 localStorage 키다(로그인 전이거나 주입하지 않으면 anonymous 키). 사용하는 페이지가 주입한다.
  bookmarkUid?: string | null;
  // 스코프 소유 여부다. 같은 컨트롤러를 두 번 그릴 때(고급 필터 시트 프리뷰) 프리뷰에서 켜면,
  // 프리뷰가 기본 스코프로 덮어써 북마크·기억값이 바뀐다. 그래서 페이지의 레일 하나만 소유한다.
  ownsBookmarkScope?: boolean;
};

// Dataset / 북마크 / Review 가 함께 쓰는 필터 레일이다(검색 행 아래 2행). 다음만 담는다:
// - 북마크된 컨트롤(프리셋 칩 + facet 드롭다운, 순서 = 북마크 순서)
// - 적용 조건 ✕ 칩 행
// 결과 수는 여기 없다. 표 위 도구 띠(BulkActionBar)가 일괄 액션 오른쪽에 표시한다(rules.md §17).
// 칩 행은 레일 안팎과 관계없이 지금 적용된 모든 조건(이름 검색 포함)을 표시한다. 학습 세션 필터
// 바와 같은 규약이다. 컨트롤과 칩은 같은 상태의 두 표현이다. 그래서 칩 ✕ 로 해제한 조건은
// 컨트롤에서도 즉시 해제된다. 칩 행 끝의 초기화는 칩이 하나라도 있을 때만 표시하고, 이름 검색까지 모두 비운다.
// 고급 필터 진입 버튼과 시트는 검색 행의 DatasetFilterAdvancedButton 이 담당한다. 북마크와 시트
// 열림은 컨트롤러가 소유한다. 그래서 레일과 버튼(다른 표시 계층)이 같은 상태를 본다. 북마크는
// 노출 목록만 저장하고 값은 저장하지 않는다(uid·표면 스코프 localStorage). 상태는
// DatasetFilterController 가 소유하고, 이 컴포넌트는 구독해서 표시만 한다.
export function DatasetFilter({
  controller,
  className,
  bookmarkSurface = "datasetLibrary",
  bookmarkUid = null,
  ownsBookmarkScope = true,
}: DatasetFilterProps): ReactNode {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  // uid 는 로그인이 끝난 뒤 늦게 채워질 수 있다. 컨트롤러가 스코프 변경을 멱등으로 처리한다.
  useEffect(() => {
    // 프리뷰(고급 필터 시트)는 스코프를 소유하지 않는다. 기본값으로 덮어쓰면 북마크가 바뀐다.
    if (!ownsBookmarkScope) {
      return;
    }
    controller.setBookmarkScope(bookmarkUid, bookmarkSurface);
  }, [controller, bookmarkUid, bookmarkSurface, ownsBookmarkScope]);

  const groups = snapshot.filter.groups;
  const selection = snapshot.filter.selection;
  const groupValues = groups.map((group) => group.value);
  const rail = resolveFilterRail(
    snapshot.bookmarks,
    groups,
    selection,
    snapshot.labelTokens.length,
  );
  // 조합 방식 배지다. predicate 가 2개 이상일 때만 AND/OR 에 따라 결과가 다르므로 그때만 표시한다.
  // 1개 이하이면 두 모드의 결과가 같아서 숨긴다(최소 정보 원칙).
  const matchModeSignificant = countFilterPredicates(selection) + snapshot.labelTokens.length >= 2;
  // 적용 조건 칩 행이다. 선택이 있는 그룹을 모두 표시한다(순서 = 카탈로그 = CURATED_FACETS 순서).
  const appliedGroups = groups.filter((group) => (selection[group.value] ?? []).length > 0);

  return (
    <div className={[styles.filter, className ?? ""].filter(Boolean).join(" ")}>
      {/* 북마크 레일이다. 프리셋 칩만 가로 스크롤 영역에 넣어 low-res 에서도 모두 보이게 한다.
          드롭다운 메뉴는 position:absolute 라서 overflow 컨테이너에 넣으면 잘린다. 그래서 스크롤 밖에 둔다. */}
      <div className={styles.presetsRow}>
        <Scrollbar orientation="horizontal" gutter="auto" className={styles.presetsScroll}>
          {rail.presets.map((preset) => {
            const active = isPresetActive(preset, selection);
            // 대상 facet 이 없는 목록에서는 눌러도 동작하지 않으므로 비활성화한다.
            const available = isPresetAvailable(preset, groupValues);
            return (
              <button
                key={preset.id}
                type="button"
                className={styles.presetChip}
                data-active={active ? "true" : undefined}
                aria-pressed={active}
                disabled={snapshot.loading || !available}
                onClick={() => applyPreset(controller, selection, preset)}
              >
                <preset.Icon className={styles.icon} aria-hidden="true" />
                <span>{preset.label}</span>
              </button>
            );
          })}
        </Scrollbar>

        {rail.facetGroups.map((group) => (
          <FacetControl
            key={group.value}
            controller={controller}
            group={group}
            selectedValues={selection[group.value] ?? []}
            disabled={snapshot.loading}
          />
        ))}

        {matchModeSignificant ? (
          <MatchModeChip
            mode={snapshot.matchMode}
            disabled={snapshot.loading}
            onToggle={() => controller.setMatchMode(snapshot.matchMode === "all" ? "any" : "all")}
          />
        ) : null}

        {snapshot.loading && groups.length === 0 ? (
          <span className={`${styles.catalogStatus} ${inlineLoadingLineClassName}`} role="status">
            <Spinner size="sm" className={inlineSpinnerClassName} />
            라벨 필터를 불러오고 있습니다.
          </span>
        ) : null}
        {snapshot.errorMessage ? (
          <span className={noticeChipClassName} role="status" title={snapshot.errorMessage}>
            라벨 필터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </span>
        ) : null}
      </div>

      {/* 적용 조건 요약이다. 컨트롤 행 아래의 ✕ 칩 행이다. 조건을 하나씩 해제할 수 있고, 행 끝의
          초기화는 전부를 비운다. */}
      {appliedGroups.length > 0 || snapshot.labelTokens.length > 0 || snapshot.search.hasQuery ? (
        <div className={styles.appliedRow}>
          <AppliedFilterChips
            controller={controller}
            groups={appliedGroups}
            selection={selection}
            labelTokens={snapshot.labelTokens}
            searchQuery={snapshot.search.query}
            disabled={snapshot.loading}
            label="적용 중인 필터"
          />
          {/* 시트 프리뷰(스코프를 소유하지 않음)에는 표시하지 않는다. 표시하면 시트 푸터의 초기화
              (기본값 복원)와 함께 뜻이 다른 버튼 두 개가 같은 영역에 생긴다. */}
          {ownsBookmarkScope ? (
            <IconButton
              text="초기화"
              size="sm"
              variant="ghost"
              className={styles.clearAll}
              disabled={snapshot.loading}
              onClick={() => controller.clearAll()}
            >
              <RotateCcwIcon />
            </IconButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
